import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import { CardComponent } from '@custom-components/general/card';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

interface AlarmSetting {
  configGroup: string;
  showInPortal: boolean;
  emailNotification: boolean;
  afterDays: number | null;
}

interface AlarmSettingRow extends Record<string, unknown> {
  label: string;
  showInPortal: boolean;
  emailNotification: boolean;
  after: string;
}

const GROUP_LABELS = 'alarmsPage.settings.alarmTypes.groupLabels.';

const ALARMS: AlarmSetting[] = [
  { configGroup: 'no_data_alarm', showInPortal: true, emailNotification: true, afterDays: 2 },
  {
    configGroup: 'production_stopped',
    showInPortal: true,
    emailNotification: false,
    afterDays: null,
  },
  {
    configGroup: 'pv_underperformance',
    showInPortal: true,
    emailNotification: false,
    afterDays: null,
  },
];

const readOnly = (): boolean => true;

const COLUMNS: TableColumnConfig[] = [
  {
    field: 'label',
    label: 'alarmsPage.settings.alarmTypes.columns.type',
    type: TableColumnType.Text,
    bold: true,
    width: '34%',
  },
  {
    field: 'showInPortal',
    label: 'alarmsPage.settings.alarmTypes.columns.showInPortal',
    type: TableColumnType.Toggle,
    width: '20%',
    disabledWhen: readOnly,
  },
  {
    field: 'emailNotification',
    label: 'alarmsPage.settings.alarmTypes.columns.email',
    type: TableColumnType.Toggle,
    width: '26%',
    disabledWhen: readOnly,
  },
  {
    field: 'after',
    label: 'installations.alarmSettings.after',
    type: TableColumnType.Text,
    width: '20%',
  },
];

// No alarm is raised per installation yet, so the card shows the settings it will offer, fixed
// and read-only.
@Component({
  selector: 'xemex-installation-alarm-settings-card',
  imports: [CardComponent, TableComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-alarm-settings-card.component.html',
})
export class InstallationAlarmSettingsCardComponent {
  private readonly translate = inject(TranslateService);

  protected readonly columns = COLUMNS;

  // Table cells are not translated, so the rows follow the language here.
  protected readonly rows = toSignal(
    this.translate
      .stream(ALARMS.map(alarm => GROUP_LABELS + alarm.configGroup))
      .pipe(map((labels: Record<string, string>) => ALARMS.map(alarm => this.row(alarm, labels)))),
    { initialValue: [] },
  );

  private row(alarm: AlarmSetting, labels: Record<string, string>): AlarmSettingRow {
    return {
      label: labels[GROUP_LABELS + alarm.configGroup],
      showInPortal: alarm.showInPortal,
      emailNotification: alarm.emailNotification,
      after:
        alarm.afterDays === null
          ? ''
          : this.translate.instant('installations.alarmSettings.afterDays', {
              count: alarm.afterDays,
            }),
    };
  }
}
